/// <amd-module name="Repos/Views/Settings/Branches/Components/SharedFocusRow/ShareFocusRow" />
import React = require("react");
import { css } from "VSS/Platform/Layout";
import { FocusWithin, IFocusWithinStatus } from "VSSUI/FocusWithin";
import { getDefaultLinkProps, ILinkProps } from "VSSUI/Link";
import { ITableRowDetails } from "VSSUI/Table";
import { getSafeId } from "VSSUI/Util";
import { getTabIndex } from "VSSUI/Utilities/Focus";

interface IShareFocusRowProps<T> {
    className?: string;
    details: ITableRowDetails<T>;
    focusZoneId?: string;
    index: number;
    indexOffset?: number;
    linkProps: ILinkProps;
}

export function ShareFocusRow<T>(props: IShareFocusRowProps<T> & { children?: React.ReactNode }): JSX.Element {
    const onFocus = function (event: React.FocusEvent<HTMLElement>) {
        props.details.onFocusItem(props.index, event);
    };

    const [rowElement] = React.useState(() => React.createRef<HTMLTableRowElement | HTMLAnchorElement>());
    const { details, index, linkProps } = props;
    const {
        selectableText,
        ariaLabel,
        ariaBusy,
        ariaDescribedBy,
        ariaPosInSet,
        ariaRowOffset,
        ariaSetSize,
        excludeFocusZone,
        id,
        renderSpacer,
        role,
        selection,
        singleClickActivation
    } = details;

    let ariaChecked: boolean | undefined;
    let ariaSelected: boolean | undefined;
    if (role === "menuitemcheckbox") {
        ariaChecked = selection && selection.selected(index);
    } else {
        ariaSelected = selection && selection.selected(index);
    }

    return (
        <FocusWithin onFocus={onFocus}>
            {(focusStatus: IFocusWithinStatus) => {
                return (
                    <a
                        aria-busy={ariaBusy}
                        aria-checked={ariaChecked}
                        aria-describedby={ariaDescribedBy}
                        aria-label={ariaLabel}
                        aria-posinset={ariaPosInSet === null ? undefined : ariaPosInSet}
                        aria-selected={ariaSelected}
                        aria-setsize={ariaSetSize === null ? undefined : ariaSetSize}
                        className={css(
                            props.className,
                            "bolt-table-row bolt-list-row",
                            index === 0 && "first-row",
                            focusStatus.hasFocus && "focused",
                            selection && selection.selected(index) && "selected",
                            singleClickActivation && "single-click-activation",
                            linkProps && "v-align-middle",
                            selectableText && "selectable-text"
                        )}
                        data-focuszone={excludeFocusZone || (selection && !selection.selectable(index)) ? undefined : props.focusZoneId}
                        data-row-index={index}
                        id={getSafeId(id)}
                        tabIndex={!props.indexOffset ? getTabIndex(details) : -1}
                        onBlur={focusStatus.onBlur}
                        onFocus={focusStatus.onFocus}
                        ref={rowElement as any}
                        {...getDefaultLinkProps(linkProps)}
                        role={role || "row"}
                    >
                        <td
                            aria-hidden="true"
                            key="left-spacer"
                            className="bolt-table-cell-compact bolt-table-cell bolt-list-cell bolt-table-spacer-cell"
                            role="presentation"
                        >
                            {renderSpacer && renderSpacer(index, true)}
                        </td>
                        {props.children}
                        <td
                            aria-hidden="true"
                            key="right-spacer"
                            className="bolt-table-cell-compact bolt-table-cell bolt-list-cell bolt-table-spacer-cell"
                            role="presentation"
                        >
                            {renderSpacer && renderSpacer(index, false)}
                        </td>
                    </a>
                );
            }}
        </FocusWithin>
    );
}
